/**
 * OfferGeneratorCard
 *
 * "Creatore di Offerte Irresistibili" — free/pro client tool that generates
 * ONE marketing offer via Claude Haiku 4.5 (backend: /api/client/offer).
 *
 * Gated by the same "profile complete" flag as the other AI cards (the
 * LockedToolOverlay in DashboardHome hides it when positioning is missing).
 *
 * Flow:
 *  1. GET /client/offer → tool config, cooldown status, last generated offer
 *  2. User fills 5 fields (servizio, prezzo, obiettivo, vantaggio, canale)
 *  3. POST /client/offer/generate → single offer object
 *  4. UI shows result + copy buttons; new generation locked until cooldown
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Sparkles, Copy, Check, RefreshCw, Lock, Gift, Loader2 } from "lucide-react";
import { AiBadge } from "./AiBadge";



const OBIETTIVI = [
  "Acquisire nuove clienti",
  "Riempire i momenti morti",
  "Fidelizzare le clienti",
  "Aumentare lo scontrino medio",
  "Promuovere un nuovo servizio",
];

const VANTAGGI = [
  "Piccolo sconto",
  "Trattamento incluso",
  "Consulenza gratuita",
  "Prodotto omaggio",
  "Servizio aggiuntivo",
  "Pacchetto di servizi",
  "Nessuna preferenza",
];

const CANALI = ["Instagram", "Facebook", "WhatsApp", "Email", "Nel salone"];

function daysUntil(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = then - now;
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function offerToPlainText(offer) {
  if (!offer) return "";
  const s = offer.struttura || {};
  const stories = (offer.stories_instagram || [])
    .map((t, i) => `Story ${i + 1}: ${t}`)
    .join("\n");
  return [
    `🎁 ${offer.nome_offerta}`,
    "",
    "STRUTTURA",
    `Cosa comprende: ${s.cosa_comprende || ""}`,
    `Vantaggio: ${s.vantaggio || ""}`,
    `Scadenza: ${s.scadenza || ""}`,
    "",
    "TESTO PROMOZIONALE",
    offer.testo_promozionale || "",
    "",
    "WHATSAPP",
    offer.messaggio_whatsapp || "",
    "",
    "STORIES INSTAGRAM",
    stories,
    "",
    `CTA: ${offer.call_to_action || ""}`,
    "",
    "PERCHÉ FUNZIONA",
    offer.perche_funziona || "",
  ].join("\n");
}

async function copyText(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiato`);
  } catch {
    toast.error("Impossibile copiare — copia a mano");
  }
}

export default function OfferGeneratorCard() {
  const [state, setState] = useState(null); // { enabled, unlocked, cooldown, last_offer, profile }
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    servizio: "",
    prezzo: "",
    obiettivo: OBIETTIVI[0],
    vantaggio: VANTAGGI[0],
    canale: CANALI[0],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/offer");
      setState(data);
      // If no offer yet and tool is available, open the form by default
      if (!data.last_offer && data.cooldown?.available) setShowForm(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.servizio.trim() || !form.prezzo) {
      toast.error("Compila servizio e prezzo");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/client/offer/generate", {
        servizio: form.servizio.trim(),
        prezzo: Number(form.prezzo),
        obiettivo: form.obiettivo,
        vantaggio: form.vantaggio,
        canale: form.canale,
      });
      setState((prev) => ({
        ...(prev || {}),
        last_offer: data.offer,
        cooldown: data.cooldown,
      }));
      setShowForm(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="offer-card-loading"
        className="rounded-3xl border border-[var(--mp-border)] bg-white p-8 text-center text-[var(--mp-muted)]"
      >
        <Loader2 className="h-5 w-5 mx-auto animate-spin" />
      </section>
    );
  }

  if (!state?.enabled) {
    return null; // admin disabled — hide entirely
  }

  const cooldownAvailable = state.cooldown?.available;
  const daysLeft = cooldownAvailable ? 0 : daysUntil(state.cooldown?.next_available_at);
  const offer = state.last_offer;

  return (
    <section
      data-testid="offer-generator-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm overflow-hidden"
    >
      <header className="px-6 py-5 border-b border-[var(--mp-border)] flex items-start gap-4 flex-wrap">
        <div className="h-11 w-11 rounded-2xl bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center border border-[var(--mp-ink)] shrink-0">
          <Gift className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Strumento esclusivo · sbloccato con posizionamento
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
            Creatore di Offerte Irresistibili
          </h2>
          <p className="text-sm text-[var(--mp-muted)] mt-1.5 leading-relaxed">
            Un'offerta credibile per il tuo salone, costruita sul tuo
            posizionamento.{" "}
            <strong>
              {state.max_per_week === 1
                ? "1 generazione a settimana"
                : `${state.max_per_week} generazioni a settimana`}
            </strong>{" "}
            — usala bene.
          </p>
          {state.cooldown && state.cooldown.max > 1 && state.cooldown.used > 0 && (
            <div
              data-testid="offer-usage-counter"
              className="mt-2 text-xs text-[var(--mp-ink)] bg-[var(--mp-dash-bg)] rounded-full px-3 py-1 inline-block font-semibold"
            >
              {state.cooldown.used} {state.cooldown.used === 1 ? "offerta" : "offerte"}{" "}
              già{" "}
              {state.cooldown.used === 1 ? "fatta" : "fatte"} questa settimana,{" "}
              {state.cooldown.remaining > 0
                ? `ne ${state.cooldown.remaining === 1 ? "resta" : "restano"} ${state.cooldown.remaining}`
                : "quota esaurita"}
              .
            </div>
          )}
        </div>
      </header>

      <div className="p-6 space-y-5">
        {/* Form (visible when no offer yet OR user clicked "Nuova offerta") */}
        {showForm && cooldownAvailable && (
          <form
            data-testid="offer-form"
            onSubmit={submit}
            className="space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Servizio da promuovere">
                <Input
                  data-testid="offer-input-servizio"
                  value={form.servizio}
                  onChange={(e) => setForm({ ...form, servizio: e.target.value })}
                  placeholder="Es. Balayage + piega"
                  maxLength={120}
                  className="rounded-full border-[var(--mp-ink)]"
                />
              </Field>
              <Field label="Prezzo normale (€)">
                <Input
                  data-testid="offer-input-prezzo"
                  type="number"
                  min="1"
                  step="1"
                  value={form.prezzo}
                  onChange={(e) => setForm({ ...form, prezzo: e.target.value })}
                  placeholder="Es. 150"
                  className="rounded-full border-[var(--mp-ink)]"
                />
              </Field>
              <Field label="Obiettivo dell'offerta">
                <Select
                  testId="offer-select-obiettivo"
                  value={form.obiettivo}
                  onChange={(v) => setForm({ ...form, obiettivo: v })}
                  options={OBIETTIVI}
                />
              </Field>
              <Field label="Vantaggio da offrire">
                <Select
                  testId="offer-select-vantaggio"
                  value={form.vantaggio}
                  onChange={(v) => setForm({ ...form, vantaggio: v })}
                  options={VANTAGGI}
                />
              </Field>
              <Field label="Dove la pubblicherai?">
                <Select
                  testId="offer-select-canale"
                  value={form.canale}
                  onChange={(v) => setForm({ ...form, canale: v })}
                  options={CANALI}
                />
              </Field>
            </div>
            <Button
              type="submit"
              data-testid="offer-generate-btn"
              disabled={generating}
              className="w-full h-12 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white font-semibold disabled:opacity-60"
            >
              {generating ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sto creando la tua offerta…
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Crea la mia offerta
                </span>
              )}
            </Button>
          </form>
        )}

        {/* Cooldown notice (form hidden, waiting) */}
        {!cooldownAvailable && !showForm && (
          <div
            data-testid="offer-cooldown-notice"
            className="rounded-2xl bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900 leading-relaxed inline-flex items-start gap-3"
          >
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">
                {state.cooldown?.max > 1
                  ? `Hai già usato tutte le ${state.cooldown.max} offerte di questa settimana.`
                  : "Hai già usato la tua offerta della settimana."}
              </div>
              <div className="text-xs mt-1">
                Prossima generazione tra <strong>{daysLeft} {daysLeft === 1 ? "giorno" : "giorni"}</strong>.
                {offer && " Intanto puoi ripescare e riusare quella qui sotto."}
              </div>
            </div>
          </div>
        )}

        {/* Offer result */}
        {offer && (
          <OfferResult
            offer={offer}
            canRegenerate={cooldownAvailable}
            onNewOffer={() => setShowForm(true)}
          />
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-4 rounded-full border border-[var(--mp-ink)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--mp-yellow)]"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function OfferResult({ offer, canRegenerate, onNewOffer }) {
  const [copied, setCopied] = useState({});
  const flash = (key) => {
    setCopied((s) => ({ ...s, [key]: true }));
    setTimeout(() => setCopied((s) => ({ ...s, [key]: false })), 1500);
  };
  const stories = offer.stories_instagram || [];
  return (
    <div data-testid="offer-result" className="space-y-4 pt-1">
      <div className="rounded-3xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)]/30 p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-ink)]">
            🎁 La tua offerta
          </div>
          <AiBadge variant="inline" />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl mt-1 leading-tight" data-testid="offer-name">
          {offer.nome_offerta}
        </h3>
      </div>

      <Section title="Struttura">
        <dl className="text-sm space-y-1.5">
          <Row k="Cosa comprende" v={offer.struttura?.cosa_comprende} />
          <Row k="Vantaggio" v={offer.struttura?.vantaggio} />
          <Row k="Scadenza" v={offer.struttura?.scadenza} />
        </dl>
      </Section>

      <Section
        title="Testo promozionale"
        onCopy={() => { copyText(offer.testo_promozionale || "", "Testo"); flash("text"); }}
        copied={copied.text}
        testId="offer-copy-text"
      >
        <p className="text-sm whitespace-pre-line" data-testid="offer-promo">
          {offer.testo_promozionale}
        </p>
      </Section>

      <Section
        title="Messaggio WhatsApp"
        onCopy={() => { copyText(offer.messaggio_whatsapp || "", "WhatsApp"); flash("wa"); }}
        copied={copied.wa}
        testId="offer-copy-whatsapp"
      >
        <p className="text-sm whitespace-pre-line" data-testid="offer-whatsapp">
          {offer.messaggio_whatsapp}
        </p>
      </Section>

      <Section
        title="Tre Stories Instagram"
        onCopy={() => { copyText(stories.map((s, i) => `Story ${i + 1}: ${s}`).join("\n"), "Stories"); flash("stories"); }}
        copied={copied.stories}
        testId="offer-copy-stories"
      >
        <ol className="text-sm list-decimal list-inside space-y-1">
          {stories.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      </Section>

      <Section title="Call to Action">
        <p className="font-semibold text-base" data-testid="offer-cta">{offer.call_to_action}</p>
      </Section>

      {offer.perche_funziona && (
        <div className="rounded-2xl bg-[var(--mp-ink)] text-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">
            💡 Perché questa offerta funziona
          </div>
          <p className="text-sm mt-1.5 leading-relaxed" data-testid="offer-why-works">
            {offer.perche_funziona}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          type="button"
          data-testid="offer-copy-all"
          onClick={() => copyText(offerToPlainText(offer), "Offerta completa")}
          className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
        >
          <Copy className="h-4 w-4 mr-1.5" />
          Copia tutto
        </Button>
        {canRegenerate && (
          <Button
            type="button"
            variant="outline"
            data-testid="offer-new"
            onClick={onNewOffer}
            className="rounded-full border-[var(--mp-ink)]"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Nuova offerta
          </Button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, onCopy, copied, testId }) {
  return (
    <div className="rounded-2xl border border-[var(--mp-border)] p-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          {title}
        </div>
        {onCopy && (
          <button
            type="button"
            data-testid={testId}
            onClick={onCopy}
            className="text-[11px] font-semibold text-[var(--mp-ink)] hover:underline inline-flex items-center gap-1"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copiato" : "Copia"}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <dt className="font-semibold text-[var(--mp-muted)] w-28 shrink-0">{k}:</dt>
      <dd>{v}</dd>
    </div>
  );
}
